import { HttpErrorDto } from "@common/dto/http";
import React from "react";
import { I18n } from "react-redux-i18n";

import { getFieldsToImport, ImportQuestionnaireParams } from "../services/questionnaires";
import { FolderContext } from "@common/dto/folder/context";
import { generateId } from "@common/utils/common";
import { FieldToImport, FieldsToImportDto } from "../dto/fields-to-import-get-dto";
import { InternalImportFieldFolderList } from "../components/import-questionnaire-management/import-questionnaire-management.types";

export type ImportQuestionnaireProps = {
  showErrorMessage: (msg: string) => void;
  showInfoMessage: (msg: string) => void;
  importQuestionnaire: (payload: ImportQuestionnaireParams) => Promise<void | { errors: Array<string> }>;
};

export const useImportQuestionnaireController = ({
  showErrorMessage,
  showInfoMessage,
  importQuestionnaire,
}: ImportQuestionnaireProps) => {
  const [importedQuestionnaireFile, setImportedQuestionnaireFile] = React.useState<File | undefined>();
  const importQuestionnaireFieldRef = React.useRef<HTMLInputElement>(null);

  const [folders, setFolders] = React.useState<Array<InternalImportFieldFolderList>>([]);
  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [fileFormatValid, setFileFormatValid] = React.useState(false);

  const supportedQuestionnaireFileExtensions = [".xml"].join(",");

  const handleCloseImportQuestionnaireManagement = React.useCallback(() => {
    setImportedQuestionnaireFile(undefined);
    setFolders([]);
  }, []);

  const handleImportQuestionnaireFile = React.useCallback(() => {
    importQuestionnaireFieldRef?.current?.click();
  }, []);

  const createFoldersTreeFromFields = React.useCallback((fields: FieldsToImportDto) => {
    const individualDataFolderFakeId = -1;
    const pedigreeDataFolderFakeId = -2;

    const nestedArray: Array<InternalImportFieldFolderList> = [
      {
        name: I18n.t("Individual Data Folders"),
        imageURL: "gif/df_individual.gif",
        imageURLSelected: "gif/df_individual.gif",
        children: [],
        context: FolderContext.individual,
        id: individualDataFolderFakeId
      },
      {
        name: I18n.t("Pedigree Data Folders"),
        imageURL: "gif/df_pedigree.gif",
        imageURLSelected: "gif/df_pedigree.gif",
        children: [],
        context: FolderContext.pedigree,
        id: pedigreeDataFolderFakeId
      },
    ];

    const fieldPathDelimiter = "\\";

    for (const fieldId in fields) {
      const item = fields[fieldId] as FieldToImport;

      const fullPathItems = item.fullPath.split(fieldPathDelimiter);

      let parentChildren: InternalImportFieldFolderList[] = nestedArray[item.context - 1]?.children || []

      fullPathItems.forEach((pathItem: string, pathIndex: number) => {
        const existingNode = parentChildren.find((node) => node.name === pathItem);

        if (existingNode) {
          parentChildren = existingNode.children!;
        } else {
          const lastItem = pathIndex === fullPathItems.length - 1;
          const newNode: InternalImportFieldFolderList = {
            id: lastItem ? fieldId : generateId(),
            name: pathItem,
            children: lastItem ? undefined : [],
            context: item.context,
            imageURL: lastItem ? item.imageURL : "gif/folder_closed.gif",
            imageURLSelected: lastItem ? item.imageURL : "gif/folder_closed.gif",
          };
          parentChildren.push(newNode);
          parentChildren = newNode.children || [];
        }
      });
    }

    return nestedArray.filter((folders) => folders.children?.length);
  }, []);

  const handleGetFieldsToImport = React.useCallback(
    async (file: File) => {
      try {
        setIsFoldersLoading(true);
        const fields = await getFieldsToImport(file);
        setFolders(createFoldersTreeFromFields(fields));
        setFileFormatValid(true);
      } catch (err) {
        console.error(err);
        showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to get fields to import"));
        setFileFormatValid(false);
      } finally {
        setIsFoldersLoading(false);
      }
    },
    [createFoldersTreeFromFields, showErrorMessage]
  );

  const handleImportedFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.item(0);

      const resetValueAfterSelected = () => (e.target.value = "");

      if (!selectedFile) {
        return;
      }
      setImportedQuestionnaireFile(selectedFile);
      resetValueAfterSelected();
      handleGetFieldsToImport(selectedFile);
    },
    [handleGetFieldsToImport]
  );

  const saveImportedQuestionnaire = async (questionnairePayload: ImportQuestionnaireParams) => {
    try {
      const importQuestionnaireResponse = await importQuestionnaire(questionnairePayload);

      const duplicateErrorMessage = "The name of this questionnaire already exists";

      if(importQuestionnaireResponse?.errors?.length && !importQuestionnaireResponse.errors[0]?.includes(duplicateErrorMessage)) {
        throw {
          entity: importQuestionnaireResponse?.errors[0]
        }
      }

      if(!importQuestionnaireResponse?.errors?.length) {
        handleCloseImportQuestionnaireManagement();
        showInfoMessage(I18n.t("Format has been successfully imported"));
      }

      return importQuestionnaireResponse;

    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to save imported questionnaire"));
    }
  };

  return {
    importedQuestionnaireFile,
    handleCloseImportQuestionnaireManagement,
    handleImportQuestionnaireFile,
    handleImportedFileChange,
    saveImportedQuestionnaire,
    importQuestionnaireFieldRef,
    supportedQuestionnaireFileExtensions,
    folders,
    isFoldersLoading,
    fileFormatValid
  };
};
